import {
  AsyncAutocomplete,
  AsyncAutocompleteFetchOptionsFunction,
  Button,
  ListItem,
  ListItemText,
  Stack,
} from '@cmg/design-system';
import React from 'react';

import ServerErrorsBanner from '../../../../../../../common/components/indicators/server-error/ServerErrorsBanner';
import { InvestorContact } from '../InvestorContactInformationForm.model';
import { useInvestorContactSearch_InvestorFirmsEmployeesLazyQuery } from './graphql/__generated__';
import {
  getContactsOptions,
  getInvestorContactSearchTextFilter,
  InvestorContactOption,
} from './InvestorContactSearch.model';
import { NoOptions } from './no-options/NoOptions';

const PAGE_SIZE = 25;

const EMPTY_RESULT = { data: [] as InvestorContactOption[], pagination: { hasNext: false } };

export type Props = {
  handleAddContact: (item: InvestorContact) => void;
  bankInvestorKey: string | undefined;
  disabled?: boolean;
  isCoveredAccount: boolean;
  existingContacts: InvestorContact[];
};

const InvestorContactSearch: React.FC<Props> = ({
  handleAddContact,
  bankInvestorKey,
  disabled,
  isCoveredAccount,
  existingContacts,
}) => {
  const [loadContacts, { error, loading }] =
    useInvestorContactSearch_InvestorFirmsEmployeesLazyQuery();

  const handleFetchOptions: AsyncAutocompleteFetchOptionsFunction<InvestorContactOption> =
    React.useCallback(
      async (searchText: string, loadedPage: number = 1) => {
        if (!bankInvestorKey) {
          return EMPTY_RESULT;
        }

        const { data, error: fetchError } = await loadContacts({
          variables: {
            firmKey: bankInvestorKey,
            isCoveredAccount,
            where: getInvestorContactSearchTextFilter(searchText),
            skip: (loadedPage - 1) * PAGE_SIZE,
            take: PAGE_SIZE,
          },
          fetchPolicy: 'no-cache',
        });

        // The lazy query resolves on error with the last successful data, so it must not be used
        if (fetchError) {
          return EMPTY_RESULT;
        }

        const grid = isCoveredAccount ? data?.coveredInvestorFirmsGrid : data?.investorFirmsGrid;
        const segment = grid?.items?.[0]?.investorContacts;

        return {
          data: getContactsOptions(segment?.items ?? []),
          pagination: { hasNext: segment?.pageInfo.hasNextPage ?? false },
        };
      },
      [loadContacts, bankInvestorKey, isCoveredAccount]
    );

  const noOptionsComponent = React.useMemo(() => {
    return (
      <NoOptions
        handleCreateNew={() => handleAddContact({ name: '', email: '', isCustom: true })}
      />
    );
  }, [handleAddContact]);

  return (
    <Stack>
      {error && <ServerErrorsBanner error={error} />}
      <AsyncAutocomplete<InvestorContactOption, false, false>
        onFetchOptions={handleFetchOptions}
        TextFieldProps={{
          name: 'investorContact',
          label: 'Investor Contact',
          placeholder: 'Select a contact...',
        }}
        sx={{ width: '60%', marginBottom: theme => theme.spacing(2) }}
        disabled={disabled}
        isLoadingOptions={loading}
        value={null}
        footerButton={
          <Button
            variant="text"
            aria-label="create new footer"
            onClick={() => {
              handleAddContact({
                name: '',
                email: '',
                isCustom: true,
              });
            }}
          >
            Create New
          </Button>
        }
        onChange={(_, value) => {
          if (!value || typeof value === 'string') {
            return;
          }

          handleAddContact({ name: value.label, email: value.email, isCustom: false });
        }}
        blurOnSelect={true}
        renderOption={(props, option, _, ownerState) => {
          if (
            existingContacts.some(
              contact => contact.name === option.label && contact.email === option.email
            )
          ) {
            return undefined;
          }
          const { key, ...rest } = props;
          return (
            <ListItem key={key} {...rest} disablePadding>
              <ListItemText primary={ownerState.getOptionLabel(option)} secondary={option.email} />
            </ListItem>
          );
        }}
        noOptionsText={noOptionsComponent}
      ></AsyncAutocomplete>
    </Stack>
  );
};

export default InvestorContactSearch;
